import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {RenderableTitle, SurveySectionsRenderingModel} from "data/models/survey/base";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {QUESTION_CONTAINER_CLASS} from "components/Survey/classes";
import {useReactToPrint} from "react-to-print";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react";
import {faDownload} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import BrandStylesPreview from "components/BrandStyles/preview";
import Loading from "components/Loading";
import {assertNoError} from "helpers/asserts";
import {useSearchParams} from "react-router-dom";
import {useObjectVal} from "react-firebase-hooks/database";
import {BrandStylesResult, BrandVoiceResult} from "data/models/deliverables/brand-styles";
import {ref} from "firebase/database";
import {database} from "hooks/firebase";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";

export type ViewBrandAssets_SurveyQuestionPage_Props = {
  visible: boolean,
  title: RenderableTitle,
  description: RenderableTitle | null,
  surveySectionsRenderingModel: SurveySectionsRenderingModel | null,
}

export default function ViewBrandStylesSurveyQuestionPage(
    {
      visible,
      title,
      description,
      surveySectionsRenderingModel,
    }: ViewBrandAssets_SurveyQuestionPage_Props
) {
  const [searchParams] = useSearchParams();
  const auth = getAuth();
  const [user] = useAuthState(auth);
  const printContentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printContentRef,
    documentTitle: `${surveySectionsRenderingModel?.businessName} Brand Styles`,
  });
  const [autoPrinted, setAutoPrinted] = useState<boolean>(false);
  const brandStylesDeliverablePath = useMemo(() => `user-data/${user?.uid ?? ""}/deliverables/brand-styles`, [user]);
  const [brandStyles, brandStylesLoading, brandStylesError] =
      useObjectVal<BrandStylesResult>((user && ref(database, brandStylesDeliverablePath)) ?? null);
  const bandVoiceDeliverablePath = useMemo(() => `user-data/${user?.uid ?? ""}/deliverables/brand-voice/result`, [user]);
  const [brandVoice, brandVoiceLoading, brandVoiceError] =
      useObjectVal<BrandVoiceResult>((user && ref(database, bandVoiceDeliverablePath)) ?? null);
  const downloadImmediately = searchParams.has("download");

  const feedbackFormUrl = useMemo(() => `https://go.brandingcompass.com/s3/BC-BrandKit-Feedback?email=${encodeURIComponent(user?.email ?? '')}`, [user]);

  const [showUserFeedbackSurvey, setShowUserFeedbackSurvey] = useState<boolean>(false);

  const undefinedAny = (
      isNullOrUndefined(surveySectionsRenderingModel)
      || isNullOrUndefined(brandStyles)
      || isNullOrUndefined(brandVoice)
  );

  const anyLoading = (brandStylesLoading || brandVoiceLoading);
  const anError = (brandStylesError ?? brandVoiceError);

  const onSavePdf = useCallback(() => {
    handlePrint();
    setTimeout(() => setShowUserFeedbackSurvey(true), 1555);
  }, [handlePrint, setShowUserFeedbackSurvey]);

  useEffect(() => {
    if (
        !downloadImmediately
        || undefinedAny
        || printContentRef === null
        || printContentRef.current === null
        || surveySectionsRenderingModel === null
        || autoPrinted
    ) {
      return;
    }

    setAutoPrinted(true);
    onSavePdf();
  }, [
    downloadImmediately,
    undefinedAny,
    printContentRef,
    surveySectionsRenderingModel,
    handlePrint,
    autoPrinted,
  ]);

  assertNoError(anError);

  if (anyLoading) {
    return visible && (<div className={"mx-auto"}>
          <Loading className={"mt-16"} loadingText={"Loading Brand Styles..."}/>
        </div>) ||
        null;
  }

  if (undefinedAny) {
    return <></>;
  }

  return <>
    <div className={"flex flex-col w-full"}>
      <div className={QUESTION_CONTAINER_CLASS}>
        <div className={"my-4 text-center"}>{title}</div>
        {description !== null && <div className={"my-4 text-center"}>{description}</div>}
        {visible && <BrandStylesPreview
            ref={printContentRef}
            brandStyles={brandStyles}
            brandVoice={brandVoice?.voice ?? ''}
            businessName={surveySectionsRenderingModel.businessName ?? "[Business Name]"}
        />}
      </div>
      <div className={"grow flex flex-row mt-8 justify-center"}>
      <span
          className={"text-bc-blue underline"}
      ><a href={"https://www.consumerfinance.gov/consumer-tools/save-as-pdf-instructions/"} target={"_blank"}>Learn how to save as a PDF.</a></span>
      </div>
      <div className={"grow flex flex-row my-8 justify-center"}>
        <Button
            className={"ml-1 px-8 mb-12 rounded-full"}
            color={"blue"}
            onClick={onSavePdf}
        >
          <span className={"text-xl font-bold"}>Download</span>
          <FontAwesomeIcon icon={faDownload} className={"ml-4 text-white"} size={"2xl"}/>
        </Button>
      </div>
    </div>
    <Modal className="relative" show={showUserFeedbackSurvey} onClose={() => setShowUserFeedbackSurvey(false)} popup>
      <ModalHeader className={"absolute w-full"}/>
      <ModalBody className={"mt-6"}>
        <div className="space-y-6 text-center">
          <div className={"text-3xl"}>We’d Love Your Feedback!</div>
          <p className="text-base leading-relaxed text-gray-500">
            Thank you for trying Branding Compass!
          </p>
          <p className="text-base leading-relaxed text-gray-500">
            Your insights are incredibly valuable and will help us improve.
          </p>
          <p className="text-base leading-relaxed text-gray-500">
            Please take a moment to complete <a
              className="text-bc-blue underline"
              href={feedbackFormUrl}
              target="_blank"
          >a short survey</a> — it only takes 2–3 minutes.
          </p>
          <p className="text-base leading-relaxed text-gray-500">
            Your feedback makes a big difference.
          </p>
        </div>
      </ModalBody>
      <ModalFooter className={"justify-center"}>
        <Button onClick={() => {
          window.open(feedbackFormUrl, '_blank', 'noopener,noreferrer');
          setShowUserFeedbackSurvey(false);
        }}>Open Survey</Button>
      </ModalFooter>
    </Modal>  </>
}
